import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Mail, MessageSquare, Users, Zap, BarChart3, Megaphone, Heart } from 'lucide-react';

const EmailSMSMarketing = () => {
  const quoteRef = useRef(null);
  const contentRef = useRef(null);
  const processRef = useRef(null);
  
  const isQuoteInView = useInView(quoteRef, { once: true, margin: "-100px" });
  const isContentInView = useInView(contentRef, { once: true, margin: "-100px" });
  const isProcessInView = useInView(processRef, { once: true, margin: "-100px" });

  const services = [
    { title: "Your Brand, Amplified", icon: Megaphone },
    { title: "Fresh Customer Retention", icon: Heart },
  ];

  const processSteps = [
    {
      title: "Segmentation",
      description: "Grouping your audience based on behavior, preferences, and demographics allows us to tailor campaigns that speak to individual needs.",
      icon: Users
    },
    {
      title: "Automation Setup",
      description: "Setting up automated email sequences and SMS flows for onboarding, abandoned cart recovery, and re-engagement campaigns.",
      icon: Zap
    },
    {
      title: "Creative Content",
      description: "Crafting persuasive copy and eye-catching visuals that resonate with your audience and encourage them to take action.",
      icon: Mail
    },
    {
      title: "Testing & Optimization",
      description: "A/B testing subject lines, calls to action, and layouts to continuously improve open and conversion rates.",
      icon: MessageSquare
    },
    {
      title: "Analytics & Reporting",
      description: "Regular reporting on click-through rates, conversion rates, and ROI to ensure campaigns are driving maximum engagement and sales.",
      icon: BarChart3
    }
  ];

  return (
    <div className="relative min-h-screen bg-[#b3ebf2] overflow-hidden px-4 sm:px-6">
      {/* Animated background elements */}
      <motion.div className="absolute inset-0">
        <motion.div 
          className="absolute -top-40 -right-40 w-60 sm:w-80 h-60 sm:h-80 bg-[#ffd54f] rounded-full mix-blend-multiply filter blur-xl opacity-30"
          animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
        <motion.div 
          className="absolute -bottom-40 -left-40 w-60 sm:w-80 h-60 sm:h-80 bg-[#81deea] rounded-full mix-blend-multiply filter blur-xl opacity-30"
          animate={{ scale: [1.2, 1, 1.2], rotate: [90, 0, 90] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear", delay: 10 }}
        />
      </motion.div>

      <motion.div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_2px,transparent_2px),linear-gradient(90deg,rgba(0,0,0,0.02)_2px,transparent_2px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"/>

      <div className="relative max-w-7xl mx-auto pt-20 sm:pt-32 pb-16">
        {/* Breadcrumb */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-7xl mx-auto mb-8 text-gray-600"
        >
          
        </motion.div>

        {/* Quote Section */}
        <motion.div
          ref={quoteRef}
          initial={{ opacity: 0, y: 20 }}
          animate={isQuoteInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-8 bg-[#f4b400] bg-clip-text text-transparent">
            Email & SMS Marketing
          </h1>
          <p className="text-xl md:text-2xl italic text-gray-600">
            "If you don't drive your business, you will be driven out of business."
            <span className="block mt-2 text-base">— B.C. Forbes</span>
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          className="max-w-7xl mx-auto grid grid-cols-2 gap-4 mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="p-4 text-center"
            >
              <service.icon className="w-8 h-8 mx-auto mb-2 text-[#ffd54f]" />
              <h3 className="text-sm md:text-base font-medium text-gray-800">{service.title}</h3>
            </motion.div>
          ))}
        </motion.div>

        {/* Main Content */}
        <motion.div
          ref={contentRef}
          initial={{ opacity: 0, y: 20 }}
          animate={isContentInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto mb-16 text-gray-700"
        >
          <p className="text-lg leading-relaxed mb-8">
            Your email list is your hidden goldmine. Your most valuable asset. We take it and turn it into a high-converting, automated sales machine that sells 24/7. Our email sequences keep your customers engaged, buying, and raving about your brand. SMS? That's where <span className="font-bold text-[#f4b400]">immediacy and intimacy</span> converge for rapid results.
          </p>
        </motion.div>

        {/* Process Section */}
        <motion.div
          ref={processRef}
          initial={{ opacity: 0, y: 20 }}
          animate={isProcessInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto mb-16"
        >
          <h2 className="text-3xl font-bold mb-8 text-gray-800">Process</h2>
          <div className="space-y-6">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, x: -20 }}
                animate={isProcessInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="p-6 bg-white/80 backdrop-blur-sm rounded-xl shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-4">
                  <step.icon className="w-6 h-6 text-[#ffd54f]" />
                  <h3 className="text-xl font-semibold text-[#ffd54f]">{step.title}</h3>
                </div>
                <p className="text-gray-600 mt-2">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="max-w-4xl mx-auto text-center"
        >
          <div className="p-8 bg-white/80 backdrop-blur-sm rounded-2xl">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">GROVIYO WILL:</h2>
            <p className="text-lg text-gray-700 mb-6">
              Keep your audience engaged, nurtured, and coming back for more. We create automated flows and campaigns that help you grow relationships like fruit on a tree.
            </p>
            <p className="italic text-black font-medium">
              "Thank God I found you guys. Everything was such a headache before" -ANOTHER SHAMELESS SELF-PROMOTION
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default EmailSMSMarketing;